import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useDebounce } from '../../../../../hooks';
import { MULTI_FILTER_TYPES } from '../constants';
import { getBrands, getCategories, getContents, getSections } from '../services';
import { useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';
import type {
  VisaoGeralElasticidadeMultiFilterSelectItem,
  VisaoGeralElasticidadeMultiFilterSelectList,
  VisaoGeralElasticidadeMultiFilterType,
  VisaoGeralElasticidadeTableBody,
} from '../types';
import { buildFilterRequestPayload } from '../utils';

const FILTER_SERVICE_MAP = {
  categories: getCategories,
  sections: getSections,
  brands: getBrands,
  contents: getContents,
} as const;

const createInitialSelectList = (): VisaoGeralElasticidadeMultiFilterSelectList => ({
  list: [],
  cache: [],
  value: [],
});

const createInitialFilterState = (): Record<VisaoGeralElasticidadeMultiFilterType, VisaoGeralElasticidadeMultiFilterSelectList> => ({
  categories: createInitialSelectList(),
  sections: createInitialSelectList(),
  brands: createInitialSelectList(),
  contents: createInitialSelectList(),
});

const mapFilterResponseToOptions = (items: string[]): VisaoGeralElasticidadeMultiFilterSelectItem[] =>
  (items || []).map(item => ({
    label: item,
    value: item,
  }));

type FetchFilterParams = {
  name: VisaoGeralElasticidadeMultiFilterType;
  query?: string;
  excludeSelfFromPayload?: boolean;
};

export const useMultiFiltersSelection = () => {
  const filtersBody = useVisaoGeralElasticidadeStore(state => state.filtersBody);
  const updateSharedFilters = useVisaoGeralElasticidadeStore.getState().updateSharedFilters;
  const debounce = useDebounce(500);

  const [filterState, setFilterState] = useState(createInitialFilterState);
  const filtersBodyRef = useRef(filtersBody);
  const filterStateRef = useRef(filterState);

  filtersBodyRef.current = filtersBody;
  filterStateRef.current = filterState;

  const syncStoreWithValues = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType, values: string[]) => {
      updateSharedFilters({ [name]: values } as Partial<VisaoGeralElasticidadeTableBody>);
    },
    [updateSharedFilters],
  );

  const setList = useCallback((name: VisaoGeralElasticidadeMultiFilterType, response: string[]) => {
    const newList = mapFilterResponseToOptions(response);
    const current = filterStateRef.current[name];
    const removeDuplicate = newList.filter(item => !current.value.includes(item.value));
    const concatList = current.value.length ? current.cache.concat(removeDuplicate) : newList;
    const cache = concatList.filter(item => current.value.includes(item.value));

    setFilterState(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        list: concatList,
        cache,
      },
    }));
  }, []);

  const mutation = useMutation({
    mutationFn: async ({ name, query = '', excludeSelfFromPayload = false }: FetchFilterParams) => {
      const requestBody = excludeSelfFromPayload
        ? buildFilterRequestPayload(name, filtersBodyRef.current, query)
        : {
            ...filtersBodyRef.current,
            query,
          };

      const response = (await FILTER_SERVICE_MAP[name](requestBody)) as string[];

      if (response?.length) {
        setList(name, response);
      }

      return response;
    },
  });

  const fetchFilter = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType, query = '', excludeSelfFromPayload = false) => {
      mutation.mutate({ name, query, excludeSelfFromPayload });
    },
    [mutation],
  );

  const handleOpen = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType) => {
      fetchFilter(name, '', true);
    },
    [fetchFilter],
  );

  const onSearchFilter = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType, inputValue: string) => {
      debounce(() => {
        fetchFilter(name, inputValue, true);
      });
    },
    [debounce, fetchFilter],
  );

  const handleSelectFilter = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType, values: string[]) => {
      const current = filterStateRef.current[name];
      const cache = current.list.filter(item => values.includes(item.value));

      syncStoreWithValues(name, values);
      setFilterState(prev => ({
        ...prev,
        [name]: {
          ...prev[name],
          list: prev[name].list,
          cache,
          value: values,
        },
      }));
    },
    [syncStoreWithValues],
  );

  const applySavedFilterValues = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType, values: string[]) => {
      const options = mapFilterResponseToOptions(values);

      syncStoreWithValues(name, values);
      setFilterState(prev => ({
        ...prev,
        [name]: {
          value: values,
          cache: options,
          list: options,
        },
      }));
    },
    [syncStoreWithValues],
  );

  const handleClearFilters = useCallback(
    (name: VisaoGeralElasticidadeMultiFilterType) => {
      syncStoreWithValues(name, []);
      setFilterState(prev => ({
        ...prev,
        [name]: createInitialSelectList(),
      }));
    },
    [syncStoreWithValues],
  );

  const handleClearAllFilters = useCallback(() => {
    MULTI_FILTER_TYPES.forEach(name => {
      syncStoreWithValues(name, []);
    });
    setFilterState(createInitialFilterState());
  }, [syncStoreWithValues]);

  const fetchFilterRef = useRef(fetchFilter);
  fetchFilterRef.current = fetchFilter;

  useEffect(() => {
    MULTI_FILTER_TYPES.forEach(name => {
      fetchFilterRef.current(name, '', true);
    });
  }, []);

  return {
    categories: filterState.categories,
    sections: filterState.sections,
    brands: filterState.brands,
    contents: filterState.contents,
    isLoading: mutation.isPending,
    filterHandlers: {
      handleOpen,
      onSearchFilter,
      handleSelectFilter,
      applySavedFilterValues,
      handleClearFilters,
      handleClearAllFilters,
    },
  };
};
